/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * UPLOAD FIX BATCH (approved 15/10/2026), fault g: a document whose reading
 * failed is visible to the officer as "Not read", with the reason in plain
 * words. Two kinds:
 *   unreadable  the file is not a document that can be read (an old Office
 *               file, a program, an image that could not be converted); its
 *               verdict is kept, and reading it again cannot help
 *   failed      the reading did not complete (a failure stub); a Reprocess or
 *               Read again tries again, up to the attempt limit
 * Officer facing only: the client is never told why (the client's list shows
 * the document as received). No dashes in any text here.
 */

// The reason a failure stub carries, in plain words.
export function failedReason(analysis) {
  const raw = String((analysis && (analysis._analysisError || analysis.analysisSkippedReason)) || (Array.isArray(analysis && analysis.keyFindings) ? analysis.keyFindings.join(' ') : '') || '');
  if (/download failed|not found in storage|empty blob/i.test(raw)) return 'the file could not be fetched from storage';
  if (/file_too_large|too large|exceeds .*(size|limit)|request_too_large|413/i.test(raw)) return 'the file is too large for the reading service';
  if (/too_many_pages|pages/i.test(raw) && /limit|exceed|too many/i.test(raw)) return 'the document has more pages than the reading service accepts';
  if (/unsupported_format|media type|image format|could not process image|invalid image/i.test(raw)) return 'the reading service refused the file format';
  if (/password|encrypted/i.test(raw)) return 'the file is password protected';
  if (/no JSON|unparseable|truncat|max_tokens/i.test(raw)) return 'the reading came back incomplete';
  if (/\b(429|5\d\d)\b|overloaded|timeout|timed out|ECONNRESET|fetch failed/i.test(raw)) return 'the reading service was unavailable at the time';
  return 'the reading did not complete';
}

const isStub = (a) => {
  if (!a || typeof a !== 'object') return false;
  if (a._analysisError) return true;
  if (/analysis failed/i.test(String(a.summary || ''))) return true;
  return (Array.isArray(a.keyFindings) ? a.keyFindings : []).some(k => /^\s*Analysis failed\b/i.test(String(k)));
};

// One document's state: null when it was read, else { kind, reason }.
export function notReadState(analysis) {
  if (!analysis || typeof analysis !== 'object') return null;
  if (analysis.unreadable && analysis.unreadable.reason) return { kind: 'unreadable', reason: String(analysis.unreadable.reason) };
  if (isStub(analysis)) return { kind: 'failed', reason: failedReason(analysis) };
  if (analysis.analysisSkipped) return { kind: 'failed', reason: failedReason(analysis) };
  return null;
}

// The board's list: [{ name, kind, reason, exhausted? }].
export function documentsNotRead(entries) {
  const out = [];
  for (const e of Array.isArray(entries) ? entries : []) {
    const st = notReadState(e && e.analysis);
    if (!st) continue;
    out.push({ name: String((e && e.name) || 'Document'), kind: st.kind, reason: st.reason, ...(e && e.exhausted ? { exhausted: true } : {}) });
  }
  return out;
}

// The officer's line for the Documents panel and the case note. engine false: a case the
// document engine does not read (no Reprocess, no Read again), so the line says what to do.
export function notReadLine(item, { engine = true } = {}) {
  if (!item) return '';
  const why = String(item.reason || 'the reading did not complete').replace(/[.\s]+$/, '');
  if (item.kind === 'unreadable') return `Not read: ${why}.`;
  if (!engine) return `Not read: ${why}. Open it and assess it directly, or upload a clearer copy with Manual Upload.`;
  return item.exhausted
    ? `Not read: ${why}. It could not be read after 3 attempts; ask the client for a clearer copy, or upload one with Manual Upload.`
    : `Not read: ${why}. Use Read again, or Reprocess, to try again.`;
}

// The documents not read that are still open on the case, from the case's own
// document rows (each row carries its reading): the Next action counts them and
// the sign off gate refuses an approval while any is open. Closed when the
// reading succeeds, or by the officer's own record on the row: Accepted by MLRO
// (assessed directly) or Superseded (a newer copy replaces it). An internal
// record (never read by design) is not a document not read.
export const READ_ATTEMPTS_LIMIT = 3;
const CLOSED_ASSESSMENTS = ['Accepted by MLRO', 'Superseded'];
export function notReadOpen(documents = []) {
  const out = [];
  for (const d of (Array.isArray(documents) ? documents : [])) {
    if (!d || d.internal || CLOSED_ASSESSMENTS.includes(d.assessment)) continue;
    let a = d.analysis;
    if (typeof a === 'string') { try { a = JSON.parse(a); } catch (_) { a = null; } }
    const st = notReadState(a);
    if (!st) continue;
    out.push({ name: String(d.file_name || d.doc_type || 'Document'), kind: st.kind, reason: st.reason, ...(Number(a && a.readAttempts) >= READ_ATTEMPTS_LIMIT ? { exhausted: true } : {}) });
  }
  return out;
}
export function notReadCount(documents = []) {
  return notReadOpen(documents).length;
}
